/* eslint-disable react/no-multi-comp */
import React, { useEffect } from 'react';

import './AutomatedInsights.scss';
import { useAutomatedInsightsContext } from './context';
import classNames from '@app/ui/sports-components/utils/classNames';
import { abbreviatedDecimal } from '../../utils/formatters';
import connect from '../../utils/connect';
import { useSocialPosts } from '../hooks/useSocialPosts';
import { useBroadcastExposures } from '../hooks/useBroadcastExposures';

import { MVP_BLUEPRINT_ID } from './constants';
import {
    Card,
    Flex,
    Typography,
    Tabs,
    theme,
    Collapse,
    ConfigProvider,
    Spin,
} from '@app/ui';
import { Column } from '@ant-design/plots';
import { formatDateReport } from '../../utils/formatDateReport';

const measureLabels = {
    broadcast_count: 'Broadcast Count',
    duration_sum: 'Duration',
    exposures_sum: 'Exposures',
    mvp_avg: 'MVP',
    post_count: 'Post Count',
    engagements_sum: 'Engagements',
    impressions_sum: 'Impressions',
};

const AutomatedInsightsDetails = ({
    selectedTab,
    setSelectedTab,
    insight,
    client,
    filters: rawFilters,
}: {
    selectedTab: string;
    setSelectedTab?: (tab: string) => void;
    insight: any;
    colors: [string, string];
    client: any;
    filters: any;
}) => {
    const blueprint_id = insight?.blueprint_id;
    const isMvpInsight = blueprint_id === MVP_BLUEPRINT_ID;
    const hasBroadcastDetails = !!insight?.broadcast_details;
    const hasSocialDetails = !!insight?.social_details;
    const ref = React.useRef(null);
    const featuredSponsor = insight?.identifiers.find(
        item => item.entity === 'sponsors',
    )?.value;
    const [hoveredTab, setHoveredTab] = React.useState(null);

    const details = React.useMemo(() => {
        if (!insight) return null;
        if (!insight.broadcast_details && !insight.social_details) return null;
        if (insight.broadcast_details && !insight.social_details)
            return insight.broadcast_details;
        if (!insight.broadcast_details && insight.social_details)
            return insight.social_details;

        if (isMvpInsight) {
            return insight.social_details ?? insight.broadcast_details;
        } else {
            return selectedTab === 'broadcast'
                ? insight.broadcast_details
                : insight.social_details;
        }
    }, [selectedTab, insight]);

    const { filters, spId } = React.useMemo(() => {
        const sponsor = insight?.identifiers.find(
            identifier => identifier.entity === 'sponsors',
        );

        const spId = sponsor ? sponsor.id : null;

        const rightsholder = insight?.identifiers.find(
            identifier => identifier.entity === 'rightsholders',
        );

        const rhId = rightsholder ? rightsholder.id : null;

        const placement = insight?.identifiers.find(
            identifier => identifier.entity === 'placements',
        );

        const placementType = insight?.identifiers.find(
            identifier => identifier.entity === 'placement_types',
        );

        const plId = placement ? placement.id : null;
        const pltId = placementType ? placementType.id : null;

        return {
            filters: {
                ...(spId && { sponsorIds: [spId] }),
                ...(rhId && { rightsholderIds: [rhId] }),
                mediaTypeIds: ['1'],
                ...(plId && {
                    rightsholderPlacementIds: [`${rhId}-${plId}`],
                }),
            },
            spId,
            rhId,
            pltId,
            plId,
        };
    }, [insight]);

    const { data: socialPosts, isLoading: isLoadingSocialPosts } =
        useSocialPosts(client, filters) as {
            data: {
                posts: {
                    cdnMediaUrl: string;
                    sponsorAnnotationsDetails: {
                        sponsor: { id: string; name: string };
                        placement: { name: string };
                        rightsholder: { name: string };
                    }[];
                    averageMvpScore: number;
                }[];
            };
            isLoading: boolean;
        };

    const { data: broadcastExposures, isLoading: isLoadingBroadcastExposures } =
        useBroadcastExposures(client, filters) as {
            data: {
                broadcastRsps: {
                    frameUrl: string;
                    rightsholder: { name: string };
                    sponsor: { name: string };
                    placement: { name: string };
                    averageMvpScore: number;
                }[];
                contentDate: string;
            }[];
            isLoading: boolean;
        };

    const media = React.useMemo(() => {
        const rhs = Object.values(rawFilters.rightsholders?.list || {});

        if (hasBroadcastDetails) {
            return broadcastExposures
                ?.map(({ broadcastRsps, contentDate }) =>
                    broadcastRsps.map(
                        ({
                            frameUrl,
                            rightsholder: { name: rightsholder },
                            sponsor: { name: sponsor },
                            placement: { name: placement },
                            averageMvpScore,
                        }) => {
                            const r = rhs?.find(
                                (rh: any) => rh.name === rightsholder,
                            ) as {
                                mainLogo: { url: string };
                            };

                            const rhUrl = r?.mainLogo.url;

                            return {
                                url: frameUrl,
                                rightsholder,
                                sponsor,
                                placement,
                                mvp: averageMvpScore,
                                date: formatDateReport(contentDate),
                                rhUrl,
                            };
                        },
                    ),
                )
                .flat();
        } else {
            return socialPosts?.posts?.map(exposure => {
                const annotations = exposure.sponsorAnnotationsDetails;
                const annotation = annotations?.find(
                    annotation => `${annotation.sponsor.id}` === spId,
                );

                const url = exposure.cdnMediaUrl;

                const r = rhs?.find(
                    (rh: any) => rh.name === annotation?.rightsholder.name,
                ) as {
                    mainLogo: { url: string };
                };

                const rhUrl = r?.mainLogo.url;

                return {
                    url,
                    placement: annotation?.placement.name,
                    sponsor: annotation?.sponsor.name,
                    rightsholder: annotation?.rightsholder.name,
                    mvp: exposure.averageMvpScore,
                    date: null,
                    rhUrl,
                };
            });
        }
    }, [broadcastExposures, socialPosts, rawFilters.rightsholders]);

    const createChartDataV2 = React.useCallback((key, values) => {
        const label = measureLabels[key];

        const data = Object.entries(values)
            .map(([year, stringValue]) => ({
                [label]: parseInt(stringValue as string, 10),
                year,
            }))
            .sort((a, b) => b[label] - a[label]); // Sort descending by [label];

        return data;
    }, []);

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const tabsOptions = React.useMemo(() => {
        if (!insight) return [];

        const tabsOptions = [
            {
                name: 'broadcast',
                label: 'Broadcast',
                icon: 'tv-retro',
            },
            {
                name: 'social',
                label: 'Social',
                icon: 'images',
            },
        ];

        return tabsOptions.map(({ name, label, icon }) => {
            const isActive = selectedTab === name;
            const isHovered = hoveredTab === name;

            return {
                key: name,
                label: (
                    <div
                        onMouseEnter={() => setHoveredTab(name)}
                        onMouseLeave={() => setHoveredTab(null)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            fontSize: '14px',
                        }}
                    >
                        <i
                            className={`fa-light fa-${icon}`}
                            style={{
                                color:
                                    isActive || isHovered
                                        ? colorPrimary
                                        : '#515252',
                                width: 16,
                                height: 16,
                                fontSize: 16,
                            }}
                        />
                        {label}
                    </div>
                ),
            };
        });
    }, [selectedTab, hoveredTab]);

    const isMediaLoading =
        (hasBroadcastDetails && isLoadingBroadcastExposures) ||
        (hasSocialDetails && isLoadingSocialPosts);

    const showTabs =
        insight.broadcast_details && insight.social_details && !isMvpInsight;

    const chartContainerClasses = classNames(
        'automated-insights-cards-details-charts',
        {
            'automated-insights-cards-details-charts--grid': !isMvpInsight,
        },
    );

    return (
        <>
            {showTabs && (
                <Tabs
                    activeKey={selectedTab}
                    items={tabsOptions}
                    onChange={setSelectedTab}
                />
            )}
            <div
                style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    fontFamily: 'Roboto',
                    marginBottom: 8,
                    marginTop: !showTabs ? 16 : 0,
                }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: details?.overview,
                }}
            />

            {isMvpInsight && (
                <>
                    <Collapse
                        expandIconPosition="end"
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => (
                            <i
                                className={`fa-solid fa-chevron-${
                                    isActive ? 'up' : 'down'
                                }`}
                                style={{ fontSize: '16px', color: '#515252' }}
                            />
                        )}
                        items={[
                            {
                                key: '1',
                                label: (
                                    <Typography.Text
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: '400',
                                            fontFamily: 'Roboto',
                                        }}
                                    >
                                        <i className="fa-light fa-tv -m-r-2 " />
                                        <span className="gds-text--bold">
                                            Top Quality Exposures
                                        </span>{' '}
                                        {hasBroadcastDetails &&
                                            `- Showing Up To 20 Broadcast Exposures. To See More Go To
                        Broadcast Exposure Page.`}
                                        {hasSocialDetails &&
                                            `- Showing Up To 20 Social Exposures. To See More Go To
                        Social Media Exposure Page.`}
                                    </Typography.Text>
                                ),

                                children: (
                                    <div
                                        style={{
                                            overflowX: 'auto',
                                        }}
                                    >
                                        <div
                                            className="automated-insights-cards-details-media"
                                            style={{
                                                paddingLeft: 0,
                                                maxHeight: '400px',
                                            }}
                                        >
                                            {!isMediaLoading && (
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Card: {
                                                                bodyPadding: 0,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {media?.map((m, index) => {
                                                        const { url, rhUrl } =
                                                            m;

                                                        return (
                                                            <Card
                                                                data-test="social-post"
                                                                key={`media-${index}`}
                                                                cover={
                                                                    <div
                                                                        style={{
                                                                            height: 180,
                                                                            width: 320,
                                                                            overflow:
                                                                                'hidden',
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={
                                                                                url
                                                                            }
                                                                            alt={`Exposure ${index}`}
                                                                            style={{
                                                                                objectFit:
                                                                                    'contain',
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                paddingTop: 1,
                                                                            }}
                                                                        />
                                                                    </div>
                                                                }
                                                                style={{
                                                                    background:
                                                                        '#FEFEFE',
                                                                    minHeight:
                                                                        '200px',
                                                                }}
                                                            >
                                                                <Flex
                                                                    justify="space-between"
                                                                    style={{
                                                                        padding:
                                                                            '8px',
                                                                    }}
                                                                >
                                                                    <Flex align="center">
                                                                        <div
                                                                            style={{
                                                                                width: 32,
                                                                                height: 32,
                                                                                borderRadius: 16,
                                                                                overflow:
                                                                                    'hidden',
                                                                                border: `1px solid ${colorPrimary}`,

                                                                                marginRight: 16,

                                                                                display:
                                                                                    'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                justifyContent:
                                                                                    'center',
                                                                                color: 'white',
                                                                            }}
                                                                        >
                                                                            {rhUrl && (
                                                                                <img
                                                                                    src={
                                                                                        rhUrl
                                                                                    }
                                                                                    height="100%"
                                                                                    width="100%"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <Flex
                                                                            vertical
                                                                        >
                                                                            <Typography.Text
                                                                                style={{
                                                                                    fontSize:
                                                                                        '14px',
                                                                                    fontWeight:
                                                                                        '400',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    m.placement
                                                                                }
                                                                            </Typography.Text>
                                                                            <Typography.Text
                                                                                style={{
                                                                                    fontSize:
                                                                                        '14px',
                                                                                    fontWeight:
                                                                                        '600',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    m.sponsor
                                                                                }
                                                                            </Typography.Text>
                                                                        </Flex>
                                                                    </Flex>

                                                                    <Typography.Text
                                                                        style={{
                                                                            fontSize:
                                                                                '14px',
                                                                            fontWeight:
                                                                                '400',
                                                                        }}
                                                                    >
                                                                        {m.date}
                                                                    </Typography.Text>
                                                                </Flex>
                                                            </Card>
                                                        );
                                                    })}
                                                    {(!media ||
                                                        media.length === 0) && (
                                                        <>
                                                            {hasSocialDetails && (
                                                                <div className="automated-insights-cards-details-media-no-exposures-container">
                                                                    <p className="automated-insights-cards-details-media-no-exposures">
                                                                        <i className="fas fa-info-circle -m-h-1" />
                                                                        All
                                                                        social
                                                                        exposures
                                                                        are
                                                                        videos
                                                                        which
                                                                        are not
                                                                        yet
                                                                        available
                                                                        on this
                                                                        page. To
                                                                        see
                                                                        these
                                                                        posts,
                                                                        please
                                                                        visit
                                                                        the
                                                                        Social
                                                                        Media
                                                                        Exposure
                                                                        Page
                                                                    </p>
                                                                </div>
                                                            )}
                                                            {hasBroadcastDetails && (
                                                                <div className="automated-insights-cards-details-media-no-exposures-container">
                                                                    <p className="automated-insights-cards-details-media-no-exposures">
                                                                        <i className="fas fa-info-circle -m-h-1" />
                                                                        Broadcast
                                                                        exposures
                                                                        are not
                                                                        yet
                                                                        available
                                                                        on this
                                                                        page. To
                                                                        see
                                                                        these
                                                                        posts,
                                                                        please
                                                                        visit
                                                                        the
                                                                        Broadcast
                                                                        Exposure
                                                                        Page
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </ConfigProvider>
                                            )}
                                            {isMediaLoading && (
                                                <Spin size="large" />
                                            )}
                                        </div>
                                    </div>
                                ),
                            },
                        ]}
                    />
                    <div
                        style={{
                            marginTop: 24,
                            marginBottom: 24,
                        }}
                    >
                        <hr />
                    </div>
                </>
            )}

            <div className={chartContainerClasses} ref={ref}>
                {Object.entries(details.measures).map(([key, value]) => {
                    const data = createChartDataV2(key, value).sort(
                        (a, b) => parseInt(a.year) - parseInt(b.year),
                    );
                    const showPercentage = key === 'mvp_avg';
                    const label = measureLabels[key];

                    let title = label;

                    if (label === 'Duration') {
                        title = 'Duration (in seconds)';
                    } else if (label === 'MVP') {
                        title = 'MVP %';
                    }

                    const formatter = value => {
                        return showPercentage
                            ? `${value}%`
                            : `${abbreviatedDecimal(value)}`;
                    };

                    const containerWidth = ref.current?.clientWidth;
                    const colors = isMvpInsight
                        ? ['#5B8FF9']
                        : ['#5B8FF9', '#5AD8A6'];

                    const config = {
                        data,
                        xField: 'year',
                        yField: label,
                        style: {
                            fill: ({ year }) => {
                                if (year === featuredSponsor && isMvpInsight) {
                                    return '#00C9C9';
                                }
                            },
                        },
                        colorField: 'year',
                        axis: {
                            y: {
                                title, // Change Y-Axis Title
                                titleFontSize: 16,
                                titleFill: '#5E6066',
                                titleStroke: 'transparent',
                                labelFontSize: 16,
                                labelFill: '#5E6066',
                                labelOpacity: 1,
                                labelFormatter: formatter,
                                grid: true,
                                line: true,
                                tickLine: true,
                            },
                            x: {
                                labelFontSize: 16,
                                labelFill: '#5E6066',
                                labelOpacity: 1,
                            },
                        },
                        scale: {
                            color: {
                                range: colors,
                            },
                        },
                        interaction: {
                            tooltip: {
                                render: (e, { title, items }) => {
                                    const isFeaturedMVP =
                                        title === featuredSponsor &&
                                        isMvpInsight;

                                    const truncatedTitle =
                                        title.length > 20
                                            ? `${title.slice(0, 20)}…`
                                            : title;

                                    const capitalize = str =>
                                        str.charAt(0).toUpperCase() +
                                        str.slice(1);

                                    const tooltipTitle = isMvpInsight
                                        ? 'MVP %'
                                        : capitalize(selectedTab);

                                    return (
                                        <div
                                            key={title}
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 'bold',
                                                    marginBottom: 6,
                                                    fontSize: 12,
                                                    color: '#555',
                                                }}
                                            >
                                                {tooltipTitle}
                                            </div>

                                            {items.map(
                                                ({ value, color }, idx) => (
                                                    <div
                                                        key={idx}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            alignItems:
                                                                'center',
                                                            margin: '4px 0',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    display:
                                                                        'inline-block',
                                                                    width: 6,
                                                                    height: 6,
                                                                    borderRadius:
                                                                        '50%',
                                                                    backgroundColor:
                                                                        isFeaturedMVP
                                                                            ? '#00C9C9'
                                                                            : color,
                                                                    marginRight: 6,
                                                                }}
                                                            />
                                                            <span
                                                                style={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {truncatedTitle}
                                                            </span>
                                                        </div>
                                                        <b
                                                            style={{
                                                                marginLeft: 8,
                                                            }}
                                                        >
                                                            {formatter(value)}
                                                        </b>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    );
                                },
                            },
                        },
                        width: !isMvpInsight
                            ? containerWidth / 2
                            : containerWidth,
                        height: !isMvpInsight ? 300 : 600,
                        legend: isMvpInsight ? false : undefined,
                    };

                    return (
                        <div
                            className="automated-insights-cards-details-charts-bar"
                            style={{
                                height: !isMvpInsight ? 300 : 600,
                            }}
                            key={key}
                        >
                            <Column {...config} />
                        </div>
                    );
                })}
            </div>
        </>
    );
};

const AutomatedInsightsInfo = ({
    client,
    filters,
}: {
    client: any;
    filters: any;
}) => {
    const {
        selectedInsight: insight,
        getIcon,
        insights,
        colors,
    } = useAutomatedInsightsContext();

    const [selectedTab, setSelectedTab] = React.useState('broadcast');

    useEffect(() => {
        if (!insight) return;
        setSelectedTab('broadcast');
    }, [insight]);

    if (!insight || insights.length === 0) return null;

    const icon = getIcon(insight);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        contentPadding: 12,
                    },
                },
            }}
        >
            <Card>
                <Flex dir="row" gap={8} align="center">
                    <div
                        className="automated-insights-cards-content-card-header-icon"
                        style={{
                            backgroundColor: '#deeffd',
                            borderRadius: '50%',
                        }}
                    >
                        <img
                            src={icon as string}
                            style={{
                                padding: 4,
                            }}
                        />
                    </div>

                    <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: insight.overview,
                        }}
                        style={{
                            fontSize: '20px',
                            fontWeight: '400',
                            fontFamily: 'Roboto',
                        }}
                    />
                </Flex>

                <AutomatedInsightsDetails
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    insight={insight}
                    colors={colors as [string, string]}
                    client={client}
                    filters={filters}
                />
            </Card>
        </ConfigProvider>
    );
};

export default connect(['client', 'filters'])(AutomatedInsightsInfo);
